import React, { useState, useEffect, useCallback, useMemo } from "react";
import { recipesApi } from "../services/api";
import IngredientInput from "../components/IngredientInput";
import "./MealsPage.css";
import "./Page.css";

const MEAL_CATEGORIES = [
  "Breakfast","Mid Morning","Main Dish","Side Dish",
  "Snacks","Rice & Bread","Dessert","Drinks",
];

const CATEGORY_EMOJI = {
  "Breakfast":"🌅","Mid Morning":"🥐","Main Dish":"🥘",
  "Side Dish":"🥗","Snacks":"🍎","Rice & Bread":"🍚",
  "Dessert":"🍮","Drinks":"🥤",
};

const SORT_OPTIONS = [
  { key:"none", label:"Default" },
  { key:"calories", label:"🔥 Calories" },
  { key:"protein", label:"💪 Protein" },
  { key:"carbs", label:"🌾 Carbs" },
  { key:"fat", label:"🧈 Fat" },
  { key:"fibre", label:"🥦 Fibre" },
];

export default function MealsPage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [formDish, setFormDish] = useState("");
  const [formCategory, setFormCategory] = useState("Main Dish");
  const [formIngredients, setFormIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sortKey, setSortKey] = useState("none");
  const [sortDir, setSortDir] = useState(-1);
  const [expanded, setExpanded] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await recipesApi.search("");
      setRecipes(data);
    } catch {
      setError("Failed to load meals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const missingCount = recipes.filter(r => r.calories == null).length;

  const filtered = useMemo(() => {
    let result = recipes.filter((r) => {
      const matchCat = activeCategory === "All" || r.meal_category === activeCategory;
      const matchSearch = !search || r.dish_name.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
    if (sortKey !== "none") {
      result = [...result].sort((a, b) => {
        const av = a[sortKey] ?? -999;
        const bv = b[sortKey] ?? -999;
        return (bv - av) * sortDir;
      });
    }
    return result;
  }, [recipes, activeCategory, search, sortKey, sortDir]);

  const counts = {};
  MEAL_CATEGORIES.forEach((cat) => {
    counts[cat] = recipes.filter((r) => r.meal_category === cat).length;
  });

  const handleSortChange = (e) => {
    const key = e.target.value;
    if (key === sortKey) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleOpenAdd = () => {
    setEditingRecipe(null);
    setFormDish("");
    setFormCategory(activeCategory !== "All" ? activeCategory : "Main Dish");
    setFormIngredients([]);
    setShowForm(true);
  };

  const handleOpenEdit = (recipe, e) => {
    e.stopPropagation();
    setEditingRecipe(recipe);
    setFormDish(recipe.dish_name);
    setFormCategory(recipe.meal_category);
    setFormIngredients(recipe.ingredients.map((i) => i.ingredient_name));
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formDish.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingRecipe) {
        await recipesApi.update(editingRecipe.id, {
          dish_name: formDish.trim(),
          meal_category: formCategory,
          ingredients: formIngredients.map((name) => ({ ingredient_name: name })),
        });
        setSuccessMsg(`✅ "${formDish}" updated!`);
      } else {
        await recipesApi.create({
          dish_name: formDish.trim(),
          meal_category: formCategory,
          ingredients: formIngredients.map((name) => ({ ingredient_name: name })),
        });
        setSuccessMsg(`✅ "${formDish}" added!`);
      }
      setTimeout(() => setSuccessMsg(null), 2000);
      setShowForm(false);
      fetchRecipes();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save dish.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recipe, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${recipe.dish_name}"?`)) return;
    try {
      await recipesApi.delete(recipe.id);
      setRecipes((prev) => prev.filter((r) => r.id !== recipe.id));
    } catch {
      setError("Failed to delete dish.");
    }
  };

  const handleGetNutritionBatch = async () => {
    setBatchLoading(true);
    try {
      const result = await recipesApi.getNutritionBatch();
      setSuccessMsg(`✅ Nutrition calculated for ${result.updated} dish${result.updated !== 1 ? "es" : ""}!`);
      setTimeout(() => setSuccessMsg(null), 3000);
      await fetchRecipes();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to calculate nutrition.");
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">🍽 Meals</h1>
          <p className="page-sub">{recipes.length} dish{recipes.length !== 1 ? "es" : ""} saved</p>
        </div>
        <div className="meals-header-actions">
          {batchLoading ? (
            <button className="meals-nutrition-all-btn" disabled>
              <span className="meals-btn-spinner"></span> Calculating…
            </button>
          ) : (
            <button className="meals-nutrition-all-btn" onClick={handleGetNutritionBatch}>
              {missingCount > 0 ? `✨ Get Nutrition (${missingCount})` : "↻ Recalculate All"}
            </button>
          )}
          <button className="btn-primary" onClick={handleOpenAdd}>+ Add Dish</button>
        </div>
      </div>

      {error && <div className="error-banner" onClick={() => setError(null)}>{error} ✕</div>}
      {successMsg && <div className="success-banner">{successMsg}</div>}

      {/* Search + Sort row */}
      <div className="meals-search-row">
        <input
          type="text"
          placeholder="🔍 Search dishes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="meals-search"
        />
        <div className="meals-sort-wrap">
          <select
            className="meals-sort-select"
            value={sortKey}
            onChange={handleSortChange}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
          {sortKey !== "none" && (
            <button className="meals-sort-dir-btn"
              onClick={() => setSortDir(d => d * -1)}
              title="Toggle sort direction">
              {sortDir === -1 ? "↓" : "↑"}
            </button>
          )}
        </div>
      </div>

      {/* Category pills */}
      <div className="meals-cats">
        <button className={`meals-cat-pill ${activeCategory === "All" ? "active" : ""}`}
          onClick={() => setActiveCategory("All")}>
          All <span className="meals-cat-count">{recipes.length}</span>
        </button>
        {MEAL_CATEGORIES.map((cat) => (
          <button key={cat}
            className={`meals-cat-pill ${activeCategory === cat ? "active" : ""}`}
            onClick={() => setActiveCategory(cat)}>
            {CATEGORY_EMOJI[cat]} {cat}
            {counts[cat] > 0 && <span className="meals-cat-count">{counts[cat]}</span>}
          </button>
        ))}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="meals-form-card">
          <h3 className="meals-form-title">{editingRecipe ? "Edit Dish" : "New Dish"}</h3>
          <div className="meals-form-row">
            <div className="meals-form-group">
              <label>Dish Name *</label>
              <input type="text" placeholder="e.g. Poha, Dal Rice…"
                value={formDish} onChange={(e) => setFormDish(e.target.value)}
                autoFocus className="meals-input" />
            </div>
            <div className="meals-form-group">
              <label>Category</label>
              <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)}
                className="meals-select">
                {MEAL_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{CATEGORY_EMOJI[cat]} {cat}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="meals-form-group">
            <label>Ingredients</label>
            {formIngredients.length > 0 && (
              <div className="meals-ing-list">
                {formIngredients.map((ing, i) => (
                  <div key={i} className="meals-ing-tag">
                    {ing}
                    <button onClick={() => setFormIngredients(prev => prev.filter((_, j) => j !== i))}
                      className="meals-ing-remove">✕</button>
                  </div>
                ))}
              </div>
            )}
            <IngredientInput onAdd={(name) => setFormIngredients(prev => [...prev, name.trim()])}
              placeholder="Add ingredient…" />
          </div>
          <div className="meals-form-actions">
            <button className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || !formDish.trim()}>
              {saving ? "Saving…" : editingRecipe ? "Update" : "Save Dish"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading meals…</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🍽</div>
          <p>{search ? "No dishes match your search." : "No dishes in this category yet."}</p>
        </div>
      ) : (
        <>
          <div className="meals-grid">
            {filtered.map((recipe) => {
              const hasNutrition = recipe.calories != null;
              const isExpanded = expanded.has(recipe.id);
              const isHL = (key) => sortKey === key && hasNutrition;

              return (
                <div key={recipe.id}
                  className={`meals-card ${isExpanded ? "expanded" : ""}`}
                  onClick={() => toggleExpanded(recipe.id)}
                >
                  {/* Card top — always visible */}
                  <div className="meals-card-top">
                    <div className="meals-card-info">
                      <div className="meals-card-name">{recipe.dish_name}</div>
                      <div className="meals-card-cat">{CATEGORY_EMOJI[recipe.meal_category]} {recipe.meal_category}</div>
                    </div>
                    <div className="meals-card-right">
                      <div className="meals-card-actions" onClick={e => e.stopPropagation()}>
                        <button className="meals-edit-btn" onClick={(e) => handleOpenEdit(recipe, e)}>✏️</button>
                        <button className="meals-delete-btn" onClick={(e) => handleDelete(recipe, e)}>🗑</button>
                      </div>
                      <div className="meals-nutrition-dot"
                        style={{ background: hasNutrition ? "#22c55e" : "#2a2a3a" }}
                        title={hasNutrition ? "Nutrition available" : "No nutrition data"} />
                      <span className="meals-expand-icon">{isExpanded ? "▴" : "▾"}</span>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className="meals-card-body" onClick={e => e.stopPropagation()}>
                      {recipe.ingredients.length > 0 && (
                        <div className="meals-card-ings">
                          {recipe.ingredients.map((ing, i) => (
                            <span key={i} className="meals-ing-chip">{ing.ingredient_name}</span>
                          ))}
                        </div>
                      )}

                      {recipe.serving_size && (
                        <div className="meals-serving-badge">~{recipe.serving_size}</div>
                      )}

                      {hasNutrition ? (
                        <>
                          <div className="meals-card-nutrition">
                            {[
                              { key:"calories", label:"🔥 Cal", unit:"" },
                              { key:"protein", label:"💪 Pro", unit:"g" },
                              { key:"carbs", label:"🌾 Carb", unit:"g" },
                              { key:"fat", label:"🧈 Fat", unit:"g" },
                              { key:"fibre", label:"🥦 Fibre", unit:"g" },
                            ].map(({ key, label, unit }) => (
                              <div key={key} className="meals-cn-item">
                                <div className="meals-cn-label">{label}</div>
                                <div className={`meals-cn-value ${isHL(key) ? "highlighted" : ""}`}>
                                  {recipe[key]}{unit}
                                </div>
                              </div>
                            ))}
                          </div>
                          {recipe.nutrition_note && (
                            <div className="meals-card-note">{recipe.nutrition_note}</div>
                          )}
                        </>
                      ) : (
                        <div className="meals-no-nutrition">
                          ⚪ No nutrition yet — click "✨ Get Nutrition" above
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <button className="meals-add-card" onClick={handleOpenAdd}>
              <span className="meals-add-icon">+</span>
              <span>Add dish</span>
            </button>
          </div>
          <div className="meals-disclaimer">
            ⚠️ Nutrition estimates based on standard Indian home serving sizes. Not for medical use.
          </div>
        </>
      )}
    </div>
  );
}
